import React, { SyntheticEvent, useRef } from 'react'
import { AppBar, Tabs, Tab as MuiTab, Slide, useTheme, useMediaQuery, Theme } from '@mui/material'

import { makeStyles } from 'tss-react/mui'
import { useNavigate } from 'react-router-dom'

const GRAY_BORDER_LEFT_PADDING = '4px'

interface StyleProps {
  tabWidth: number
  topMargin?: number
  enableTopMargin?: boolean
}

const calculateTopMargin = (props: StyleProps, theme: Theme): number => {
  if (!props.enableTopMargin) return 0
  return (props.topMargin ?? 0) + theme.shape.tabs.topMargin
}

const getTopMargin = (props: StyleProps, theme: Theme, breakpoint?: string): number => {
  switch (breakpoint) {
    case 'sm':
      return calculateTopMargin(props, theme)
    case 'md':
      return props.topMargin ?? theme.shape.tabs.topMargin
    default:
      return calculateTopMargin(props, theme)
  }
}

const useStyles = makeStyles<StyleProps>()((theme, props) => ({
  tabsBar: {
    top: props.topMargin ? props.topMargin + theme.shape.tabs.topMargin : theme.shape.tabs.topMargin,
    [theme.breakpoints.down('sm')]: {
      top: getTopMargin(props, theme, 'sm'),
    },
    [theme.breakpoints.between('sm', 'lg')]: {
      top: getTopMargin(props, theme, 'md'),
      marginRight: theme.spacing(2),
      '& .MuiTabs-root': {
        paddingLeft: `calc(${theme.spacing(2)} - ${GRAY_BORDER_LEFT_PADDING})`,
      },
    },
    [theme.breakpoints.up('md')]: {
      top: getTopMargin(props, theme),
      '& .MuiTabs-root': {
        paddingLeft: `calc(${theme.spacing(2)} - ${GRAY_BORDER_LEFT_PADDING})`,
      },
    },
    zIndex: 999,
    marginLeft: 0,
    marginRight: 0,
    position: 'sticky',
    padding: 0,
    width: '100%',
    transition: theme.transitions.create('all'),
    '& .MuiTabs-scroller': {
      borderBottom: `1px solid ${theme.palette.grey.grey03}`,
    },
    '& .MuiTabs-flexContainer': {
      gap: theme.spacing(2),
    },
  },
  tabs: {
    '& .MuiTabs-scrollButtons.Mui-disabled': {
      opacity: 0.1,
    },
    '& .MuiTabs-scrollButtons': {
      width: theme.spacing(3),
    },
    '& .Mui-disabled': {
      opacity: 0.1,
    },
  },
  scrollableTab: {
    margin: 0,
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
}))

export interface Tab {
  name: React.ReactNode | string
  url: string
  queryParam?: string
  icon?: React.ReactElement
  disabled?: boolean
  dataQa?: string
  extraClass?: string
}

export interface MobileTabsProps {
  tabs: Tab[]
  activeTab: number
  elevation?: number
  additionalContent?: React.ReactNode
  topMargin?: number
  setActiveTab?: (value: number) => void
  setCurrentTab?: (value: number) => void
  setCurrPage?: (value: number) => void
  enableTopMargin?: boolean
  className?: string
  /** default: true */
  resetPageAfterTabChange?: boolean
  /** default: false */
  showOnlyIconsOnSmallSize?: boolean
}

const MobileTabs: React.FC<MobileTabsProps> = ({
  tabs,
  enableTopMargin,
  activeTab,
  elevation,
  additionalContent,
  topMargin,
  setActiveTab,
  setCurrentTab,
  setCurrPage,
  className,
  resetPageAfterTabChange = true,
  showOnlyIconsOnSmallSize = false,
}) => {
  const { classes, cx } = useStyles({ tabWidth: Math.floor(100 / tabs.length), topMargin, enableTopMargin })
  const navigate = useNavigate()
  const tabsRef = useRef<(HTMLDivElement | null)[]>([])
  const appBarRef = useRef<HTMLDivElement | null>(null)

  const theme = useTheme()
  const downSm = useMediaQuery(theme.breakpoints.down('sm'))

  const handleOnChange = (_event: SyntheticEvent, value: number): void => {
    setActiveTab?.(value)
    setCurrentTab?.(value)

    if (resetPageAfterTabChange) setCurrPage?.(0)

    const url = tabs[value].queryParam ? tabs[value].url + tabs[value].queryParam : tabs[value].url
    navigate(url)
  }

  return (
    <Slide appear={false} direction="down" in={true}>
      <AppBar className={cx(classes.tabsBar, className)} ref={appBarRef} elevation={elevation}>
        {additionalContent}
        <Tabs
          value={activeTab}
          variant={'scrollable'}
          onChange={handleOnChange}
          className={classes.tabs}
          scrollButtons={'auto'}
          allowScrollButtonsMobile
          indicatorColor="primary"
          textColor="inherit"
        >
          {tabs.map((tab, index) => (
            <MuiTab
              key={tab.url + tab.name}
              label={downSm && tab.icon !== undefined && showOnlyIconsOnSmallSize ? undefined : tab.name}
              className={cx(classes.scrollableTab, tab.extraClass)}
              ref={el => (tabsRef.current[index] = el)}
              wrapped
              disabled={tab.disabled}
              data-qa={tab.dataQa}
              icon={downSm && showOnlyIconsOnSmallSize ? tab.icon : undefined}
            />
          ))}
        </Tabs>
      </AppBar>
    </Slide>
  )
}

export default MobileTabs
